import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  addUserAddressInputSchema,
  IAddUserAddressInput,
} from "muminsmart-utils";
import PhoneInputField from "@/components/common/PhoneInputField";
import type { IState } from "country-state-city";
import { useCountryStateCity } from "@/lib/hooks/useCountryStateCity";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

export type AddressFormProps = {
  initialValues: IAddUserAddressInput;
  isSubmitting: boolean;
  submitLabel: string;
  onSubmit: unknown;
};

const ADDRESS_LABEL_OPTIONS = ["Home", "Work", "Other"] as const;
type AddressLabelOption = (typeof ADDRESS_LABEL_OPTIONS)[number];

function getAddressLabelValue(label?: string): AddressLabelOption | "" {
  if (label && ADDRESS_LABEL_OPTIONS.includes(label as AddressLabelOption)) {
    return label as AddressLabelOption;
  }

  return "";
}

export default function AddressForm({
  initialValues,
  isSubmitting,
  submitLabel,
  onSubmit,
}: AddressFormProps) {
  const geo = useCountryStateCity();
  const countries = React.useMemo(
    () => (geo ? geo.Country.getAllCountries() : []),
    [geo],
  );
  const [states, setStates] = React.useState<IState[]>([]);

  const form = useForm<IAddUserAddressInput>({
    resolver: zodResolver(addUserAddressInputSchema),
    defaultValues: {
      label: getAddressLabelValue(initialValues.label),
      fullName: initialValues.fullName,
      phone: initialValues.phone,
      senderName: initialValues.senderName || "",
      senderPhone: initialValues.senderPhone || "",
      addressLine1: initialValues.addressLine1,
      addressLine2: initialValues.addressLine2 || "",
      preferredCourier: initialValues.preferredCourier || "",
      preferredTransport: initialValues.preferredTransport || "",
      city: initialValues.city,
      state: initialValues.state,
      pincode: initialValues.pincode,
      country: initialValues.country || "India",
      isDefault: Boolean(initialValues.isDefault),
    },
  });
  const pincode = useWatch({ control: form.control, name: "pincode" });

  const normalizeLocationName = React.useCallback((value: string) => {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }, []);

  const syncLocationFields = React.useCallback(
    (countryName: string, stateName: string, cityName: string) => {
      const normalizedCountryName = normalizeLocationName(countryName);
      const matchedCountry = countries.find(
        (country) =>
          normalizeLocationName(country.name) === normalizedCountryName,
      );

      if (!matchedCountry || !geo) {
        return;
      }

      const nextStates = geo.State.getStatesOfCountry(matchedCountry.isoCode);
      setStates(nextStates);
      form.setValue("country", matchedCountry.name, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const normalizedStateName = normalizeLocationName(stateName);
      const matchedState = nextStates.find(
        (state) => normalizeLocationName(state.name) === normalizedStateName,
      );

      if (!matchedState) {
        form.setValue("state", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.setValue("city", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }

      const nextCities = geo.City.getCitiesOfState(
        matchedState.countryCode,
        matchedState.isoCode,
      );
      form.setValue("state", matchedState.name, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const normalizedCityName = normalizeLocationName(cityName);
      const matchedCity = nextCities.find((city) => {
        const normalizedName = normalizeLocationName(city.name);
        return (
          normalizedName === normalizedCityName ||
          normalizedName.includes(normalizedCityName) ||
          normalizedCityName.includes(normalizedName)
        );
      });

      form.setValue("city", matchedCity?.name ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [countries, form, geo, normalizeLocationName],
  );

  useEffect(() => {
    form.reset({
      label: getAddressLabelValue(initialValues.label),
      fullName: initialValues.fullName,
      phone: initialValues.phone,
      senderName: initialValues.senderName || "",
      senderPhone: initialValues.senderPhone || "",
      addressLine1: initialValues.addressLine1,
      addressLine2: initialValues.addressLine2 || "",
      preferredCourier: initialValues.preferredCourier || "",
      preferredTransport: initialValues.preferredTransport || "",
      city: initialValues.city,
      state: initialValues.state,
      pincode: initialValues.pincode,
      country: initialValues.country || "India",
      isDefault: Boolean(initialValues.isDefault),
    });
  }, [form, initialValues]);

  useEffect(() => {
    const selectedCountry = countries.find(
      (country) => country.name === initialValues.country,
    );

    if (!selectedCountry) {
      setStates([]);
      return;
    }

    const statesOfCountry =
      geo?.State.getStatesOfCountry(selectedCountry.isoCode) ?? [];
    setStates(statesOfCountry);

    const selectedState = statesOfCountry.find(
      (state) => state.name === initialValues.state,
    );

    if (!selectedState) {
      return;
    }
  }, [countries, geo, initialValues.country, initialValues.state]);

  useEffect(() => {
    const sanitizedPincode = `${pincode ?? ""}`.replace(/\D/g, "");
    if (sanitizedPincode.length !== 6) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${sanitizedPincode}`,
          { signal: controller.signal },
        );
        const result = (await response.json()) as Array<{
          Status: string;
          PostOffice?: Array<{
            Country: string;
            State: string;
            District: string;
            Name: string;
          }>;
        }>;
        const location = result[0]?.PostOffice?.[0];

        if (!location) {
          return;
        }

        syncLocationFields(
          location.Country,
          location.State,
          location.District || location.Name,
        );
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to resolve postal code", error);
        }
      }
    }, 350);

    return () => {
      controller.abort();
      globalThis.clearTimeout(timeoutId);
    };
  }, [pincode, syncLocationFields]);

  return (
    <Form {...form}>
      <form
        className="space-y-3"
        onSubmit={form.handleSubmit(async (values) => {
          if (typeof onSubmit === "function") {
            await onSubmit(values);
          }
        })}
      >
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Label</FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-2">
                  {ADDRESS_LABEL_OPTIONS.map((option) => {
                    const isSelected = field.value === option;

                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => field.onChange(option)}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-muted bg-white text-foreground hover:border-primary/50"
                        }`}
                        aria-pressed={isSelected}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Recipient Full Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Mufaddal"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={() => (
            <FormItem>
              <FormLabel className="font-medium">
                Recipient Phone Number
              </FormLabel>
              <FormControl>
                <Controller
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <PhoneInputField
                      country={"in"}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      inputClass={`!w-full !rounded-full !h-12 !pl-14 !pr-4 !text-base ${
                        form.formState.errors.phone ? "!border-destructive" : ""
                      }`}
                      buttonClass="!border-none !bg-transparent [&_.selected-flag]:!bg-transparent !pl-3"
                      containerClass="!w-full"
                      dropdownClass="!rounded-md"
                    />
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="senderName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">
                Sender Name (Optional)
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Sender's name"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="senderPhone"
          render={() => (
            <FormItem>
              <FormLabel className="font-medium">
                Sender Phone Number (Optional)
              </FormLabel>
              <FormControl>
                <Controller
                  control={form.control}
                  name="senderPhone"
                  render={({ field }) => (
                    <PhoneInputField
                      country={"in"}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      inputClass={`!w-full !rounded-full !h-12 !pl-14 !pr-4 !text-base ${
                        form.formState.errors.senderPhone
                          ? "!border-destructive"
                          : ""
                      }`}
                      buttonClass="!border-none !bg-transparent [&_.selected-flag]:!bg-transparent !pl-3"
                      containerClass="!w-full"
                      dropdownClass="!rounded-md"
                    />
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Address Line 1 */}
        <FormField
          control={form.control}
          name="addressLine1"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Address</FormLabel>
              <FormControl>
                <Input
                  placeholder="123 Main St"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Address Line 2 */}
        <FormField
          control={form.control}
          name="addressLine2"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">
                Address Line 2 (Optional)
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Apartment, suite, etc."
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Postal Code */}
        <FormField
          control={form.control}
          name="pincode"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Postal Code</FormLabel>
              <FormControl>
                <Input
                  placeholder="452001"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(event) => {
                    field.onChange(event.target.value.replace(/\D/g, ""));
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Country */}
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Country</FormLabel>
              <Select
                onValueChange={(value) => {
                  const country = countries.find((c) => c.name === value);
                  if (!country || !geo) return;

                  form.setValue("country", country.name);

                  const { isoCode } = country;
                  const statesOfCountry = geo.State.getStatesOfCountry(isoCode);
                  setStates(statesOfCountry);
                  form.setValue("state", "");

                  // Reset city when country changes
                  form.setValue("city", "", { shouldValidate: true });
                }}
                value={field.value}
              >
                <SelectTrigger className="w-full rounded-full p-5 border-muted bg-white text-[0.9rem]">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem value={country.name} key={country.isoCode}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* State */}
        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>State</FormLabel>
              <Select
                onValueChange={(value) => {
                  const state = states.find((s) => s.name === value);
                  if (!state || !geo) return;

                  form.setValue("state", state.name);

                  const { isoCode } = state;
                  const citiesOfState = geo.City.getCitiesOfState(
                    state.countryCode,
                    isoCode,
                  );

                  const matchedCity = citiesOfState.find(
                    (city) =>
                      normalizeLocationName(city.name) ===
                      normalizeLocationName(form.getValues("city") || ""),
                  );

                  form.setValue("city", matchedCity?.name ?? "", {
                    shouldValidate: true,
                  });
                }}
                value={field.value}
              >
                <SelectTrigger className="w-full rounded-full p-5 border-muted bg-white text-[0.9rem]">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem value={state.name} key={state.isoCode}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* City */}
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">City</FormLabel>
              <FormControl>
                <Input
                  placeholder="Indore"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="preferredCourier"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Preferred Courier</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g. Reliance, Madhur Courier, Blue Dart"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="preferredTransport"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Preferred Transport</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g. Raj Ratan Transport, Shree Ram Transport"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormItem>
              <Field orientation="horizontal">
                <Checkbox
                  id="default-checkbox"
                  name="isDefault"
                  className="h-4 w-4"
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor="default-checkbox">Set as default address</Label>
              </Field>
              <FormMessage />
            </FormItem>
          )}
        />

        <DrawerFooter className="px-0 pb-0">
          <Button
            type="submit"
            className="rounded-full"
            disabled={isSubmitting || form.formState.isSubmitting}
          >
            {isSubmitting || form.formState.isSubmitting
              ? "Saving..."
              : submitLabel}
          </Button>
        </DrawerFooter>
      </form>
    </Form>
  );
}
